import React, { Fragment, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import pluralize from 'pluralize';

import { getPrintPackageItemListingLabel } from '../../AlbumPrintPackageOption/helpers';
import { formatPriceForDisplay } from '../../../../../../../../../../util/format-money';
import { getAlbumPrintPackageTaggedItem } from '../../../helpers';
import CustomControl from '../../../../../../../../../../components/global/CustomControl';
import CheckoutItemQuantityInput from '../../../../../../../../../../services/checkout/components/CheckoutCartItem/CheckoutItemQuantityInput';
import { getPrintPackageGroupedItems } from '../../../../../../../../../../util/print-packages';

const propTypes = {
  className: PropTypes.string,
  albumPrintPackage: PropTypes.shape({
    id: PropTypes.string.isRequired,
    print_package: PropTypes.shape({
      items: PropTypes.shape({
        total: PropTypes.number.isRequired,
        records: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
            is_optional: PropTypes.bool,
            type: PropTypes.shape({
              name: PropTypes.string.isRequired,
            }),
            custom_print: PropTypes.shape({
              id: PropTypes.string.isRequired,
              name: PropTypes.string.isRequired,
            }),
            print_product_configuration: PropTypes.shape({
              print_product: PropTypes.shape({
                id: PropTypes.string.isRequired,
                name: PropTypes.string.isRequired,
              }).isRequired,
              print_product_size: PropTypes.shape({
                id: PropTypes.string.isRequired,
                print_size: PropTypes.shape({
                  id: PropTypes.string.isRequired,
                  name: PropTypes.string.isRequired,
                }).isRequired,
              }).isRequired,
            }),
          }).isRequired,
        ).isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  selectedPrintPackageItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }),
  ).isRequired,
  onPrintPackageItemToggle: PropTypes.func.isRequired,
  cartItem: PropTypes.object.isRequired,
};

const SelectablePrintPackageItemsList = ({
  className,
  albumPrintPackage,
  selectedPrintPackageItems,
  onPrintPackageItemToggle,
  cartItem,
}) => {
  const printPackage = albumPrintPackage.print_package;
  const printPackageItems = useMemo(
    () =>
      [...(printPackage?.items?.records ?? [])]?.sort((a, b) => {
        if (a?.print_product_configuration && !b?.print_product_configuration) {
          return -1;
        }

        if (!a?.print_product_configuration && b?.print_product_configuration) {
          return 1;
        }

        if (
          !a?.print_product_configuration &&
          !b?.print_product_configuration
        ) {
          return 0;
        }

        const { print_size: pzA } =
          a?.print_product_configuration?.print_product_size ?? {};
        const { print_size: pzB } =
          b?.print_product_configuration?.print_product_size ?? {};

        if (pzA && !pzB) {
          return -1;
        }

        if (!pzA && pzB) {
          return 1;
        }

        if (!pzA && !pzB) {
          return 0;
        }

        return pzA.name.localeCompare(pzB.name);
      }),
    [printPackage.items.records],
  );

  // Remove all optional items from the list as we don't want to show them.
  const filteredPrintPackageItems = useMemo(
    () =>
      printPackageItems.filter(
        printPackageItem =>
          printPackageItem.album_folder_print_package_items.total === 0,
      ),
    [printPackageItems],
  );
  const groupedPrintPackageItems = useMemo(
    () => getPrintPackageGroupedItems(filteredPrintPackageItems),
    [filteredPrintPackageItems],
  );
  const overriddenPrintPackageItem = getAlbumPrintPackageTaggedItem(
    albumPrintPackage,
  );

  const albumFolderTag = overriddenPrintPackageItem?.album_folder_tag;
  const price = overriddenPrintPackageItem?.price?.value ?? 0;
  const discountPrice = overriddenPrintPackageItem?.discount_price?.value ?? 0;
  const finalPrice = discountPrice || price;
  const classes = classNames({
    [className]: !!className,
    'selectable-album-print-package-option-items': true,
  });

  return (
    <div className={classes}>
      {Object.keys(groupedPrintPackageItems).map(printPackageItemGroupKey => {
        const printPackageItemGroup =
          groupedPrintPackageItems[printPackageItemGroupKey];

        if (printPackageItemGroup?.length > 1) {
          // This first item is only used for ref purposes.
          const [printPackageItem] = printPackageItemGroup;
          const selectedPrintPackageItemGroupItems = selectedPrintPackageItems.filter(
            item => !!printPackageItemGroup.find(({ id }) => item.id === id),
          );

          return (
            <div className="selectable-album-print-package-option-items-group">
              <CheckoutItemQuantityInput
                min={0}
                label=""
                max={printPackageItemGroup.length}
                maxDisabled={
                  selectedPrintPackageItems.length ===
                  printPackage.max_checkout_items
                }
                onChange={value => {
                  if (value > selectedPrintPackageItemGroupItems.length) {
                    const nonSelectedItems = printPackageItemGroup.filter(
                      item =>
                        !selectedPrintPackageItemGroupItems.find(
                          selectedItem => selectedItem.id === item.id,
                        ),
                    );
                    onPrintPackageItemToggle(nonSelectedItems[0]);
                  } else if (value >= 0) {
                    onPrintPackageItemToggle(
                      selectedPrintPackageItemGroupItems[0],
                    );
                  }
                }}
                value={selectedPrintPackageItemGroupItems.length}
              />
              <div className="text-sm ml-1">
                {getPrintPackageItemListingLabel(printPackageItem, true)}
              </div>
            </div>
          );
        }

        const [printPackageItem] = printPackageItemGroup;
        const isSelected = selectedPrintPackageItems.find(
          item => item.id === printPackageItem.id,
        );

        return (
          <CustomControl
            id={`${albumPrintPackage.id}-${printPackageItem.id}`}
            type="checkbox"
            shape="squared"
            className="d-block"
            size="sm"
            label={getPrintPackageItemListingLabel(printPackageItem)}
            disabled={
              !!cartItem ||
              (!isSelected &&
                selectedPrintPackageItems.length ===
                  printPackage.max_checkout_items)
            }
            checked={isSelected}
            onChange={() => {
              onPrintPackageItemToggle(printPackageItem);
            }}
          />
        );
      })}

      {
        // TODO check what happens when this case is active
        overriddenPrintPackageItem && (
          <Fragment>
            {overriddenPrintPackageItem.is_optional ? (
              <li>
                <strong>
                  Add{' '}
                  {pluralize(
                    albumFolderTag?.name ?? '',
                    overriddenPrintPackageItem.quantity,
                  )}{' '}
                  {getPrintPackageItemListingLabel(overriddenPrintPackageItem)}{' '}
                  for ${formatPriceForDisplay(finalPrice)}
                </strong>
              </li>
            ) : (
              <li>
                <strong>
                  Includes{' '}
                  {pluralize(
                    albumFolderTag?.name ?? '',
                    overriddenPrintPackageItem.quantity,
                  )}{' '}
                  {/* Since items from both sides are needed, allow the albumFolderPrintPackage to override */}
                  {getPrintPackageItemListingLabel(overriddenPrintPackageItem)}
                </strong>
              </li>
            )}
          </Fragment>
        )
      }
    </div>
  );
};

SelectablePrintPackageItemsList.propTypes = propTypes;

export default SelectablePrintPackageItemsList;
